import api from '@tyrio/api-factory';
import { DBWorkOrdersApi } from '@tyrio/dto';
import { ToastHelper, ToastMessageType, ToastType } from '@tyrio/ui-library';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { queryClient } from 'apps/app/src/app/query-client';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';

export const useCancelWorkOrder = () => {
  const mutation = useMutation(
    ['cancel_work_order'],
    (body: DBWorkOrdersApi['cancelOne']['requestBody']) =>
      api.fetch<DBWorkOrdersApi['cancelOne']>('cancel_work_order', { ...body }),
    {
      onSuccess: (data, variables) => {
        // Invalidate all work order related queries to ensure UI is updated
        queryClient.invalidateQueries('get_work_orders_paginated');
        queryClient.invalidateQueries('get_work_orders');
        queryClient.invalidateQueries('get_work_orders_with_branch_info');
        queryClient.invalidateQueries('count_work_orders_by_status');
        
        // Refresh customer order related queries
        queryClient.invalidateQueries('get_customer_orders');
        queryClient.invalidateQueries('customer_order');
        queryClient.invalidateQueries('count_customer_orders_by_status');
        
        // Invalidate sales order cache to refresh the order processing section (left side)
        const customerOrderId = variables.workOrder?.customerOrderId;
        if (customerOrderId) {
          queryClient.invalidateQueries(['sales_order', customerOrderId]);
          
          // Clear the order processing success state from local storage
          const localStorageKey = `sales_orders_purchase_${customerOrderId}`;
          const existingData = localStorage.getItem(localStorageKey);
          
          if (existingData) {
            try {
              const parsedData = JSON.parse(existingData);
              // Clear the orderResponse to reset the success state
              if (parsedData.orderResponse) {
                parsedData.orderResponse = {};
                localStorage.setItem(localStorageKey, JSON.stringify(parsedData));
              }
            } catch (error) {
              console.warn('Failed to update local storage for order processing state:', error);
            }
          }
        }

        ToastHelper.showToast(
          'Success',
          ToastType.SUCCESS,
          ToastMessageType.UPDATE,
          'Successfully cancelled work order.'
        );
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data?.error?.name || error.message || 'An error occurred!';
          ToastHelper.showToast(
            'Work orders',
            ToastType.ERROR,
            ToastMessageType.ERROR,
            errorMessage
          );
        } else {
          ToastHelper.showToast(
            'Work orders',
            ToastType.ERROR,
            ToastMessageType.ERROR,
            'An unexpected error occurred!'
          );
        }

        throw error;
      },
    }
  );

  return {
    ...mutation,
  };
};
